import { Box } from '@mui/material';
import { BarChart as MuiBarChart } from '@mui/x-charts/BarChart';
import { useTheme } from '@mui/material/styles';
import { BarChartProps } from './BarChart.props';

/**
 * BarChart Component
 *
 * A reusable bar chart component built on MUI X Charts with Material 3 design system integration.
 * Displays data in vertical bars with gradient fills, customizable axes, and grid lines.
 *
 * @example
 * ```tsx
 * const data = [
 *   { label: '2022', value: 15000 },
 *   { label: '2023', value: 17000 },
 *   { label: '2024', value: 19000 },
 * ];
 *
 * <BarChart
 *   data={data}
 *   height={241}
 *   width={387}
 *   valueFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
 * />
 * ```
 */
export const BarChart = ({
  data,
  height = 240,
  width = 387,
  yAxisLabel,
  valueFormatter = (value: number) => `$${(value / 1000).toFixed(0)}K`,
  showGrid = true,
  margin = { top: 10, right: 10, bottom: 30, left: 55 },
  sx,
  borderRadius = 8,
  colors,
  barWidth,
  barGap = 0.2,
}: BarChartProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ position: 'relative', width: `${width}px`, ...sx }}>
      {/* SVG gradients for bar fills */}
      <svg width={0} height={0} style={{ position: 'absolute' }}>
        <defs>
          {/* Cyan gradient for first bar */}
          <linearGradient id="gradient-0" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#14CEE3" />
            <stop offset="100%" stopColor="#BEF9FF" />
          </linearGradient>
          {/* Blue gradient for second bar */}
          <linearGradient id="gradient-1" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#0278FF" />
            <stop offset="50%" stopColor="#63C0FF" />
            <stop offset="100%" stopColor="#CEECFF" />
          </linearGradient>
          {/* Purple gradient for third bar */}
          <linearGradient id="gradient-2" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#4A3AFF" />
            <stop offset="100%" stopColor="#E9E7FF" />
          </linearGradient>
        </defs>
      </svg>
      <MuiBarChart
        xAxis={[
          {
            scaleType: 'band',
            data: data.map((item) => item.label),
            tickLabelStyle: {
              fontSize: 12,
              fill: theme.palette.M3.sys.light['on-surface-variant'],
              fontFamily: 'Archivo',
            },
            ...(barGap !== undefined && { categoryGapRatio: barGap }),
          },
        ]}
        yAxis={[
          {
            label: yAxisLabel,
            valueFormatter,
            tickLabelStyle: {
              fontSize: 12,
              fill: theme.palette.M3.sys.light['on-surface-variant'],
              fontFamily: 'Archivo',
            },
          },
        ]}
        series={[
          {
            data: data.map((item) => item.value),
          },
        ]}
        width={width}
        height={height}
        margin={margin}
        grid={{ horizontal: showGrid }}
        {...(barWidth && { barLabel: undefined })}
        sx={{
          '& .MuiChartsAxis-left .MuiChartsAxis-line': {
            display: 'none',
          },
          '& .MuiChartsAxis-bottom .MuiChartsAxis-line': {
            stroke: theme.palette.M3.sys.light['outline 3'],
            strokeWidth: 1,
          },
          '& .MuiChartsAxis-tick': {
            display: 'none',
          },
          '& .MuiChartsAxis-tickLabel': {
            fontSize: '12px',
            fill: theme.palette.M3.sys.light['on-surface-variant'],
            fontFamily: 'Archivo',
          },
          '& .MuiChartsGrid-line': {
            stroke: theme.palette.M3.sys.light['outline 3'],
            strokeWidth: 1,
            strokeDasharray: 'none',
          },
          '& .MuiBarElement-root': {
            rx: borderRadius,
            ry: borderRadius,
            ...(barWidth && { width: `${barWidth}px !important` }),
            '&:nth-of-type(1)': {
              fill: colors?.[0] || 'url(#gradient-0)',
            },
            '&:nth-of-type(2)': {
              fill: colors?.[1] || 'url(#gradient-1)',
            },
            '&:nth-of-type(3)': {
              fill: colors?.[2] || 'url(#gradient-2)',
            },
          },
        }}
      />
    </Box>
  );
};

export default BarChart;
